/**
 * CaseWorkspaceShell — Composed workspace with header, tab bar, and stage panels.
 *
 * Feature-flag gated: if CASE_WORKSPACE is false, renders null (existing content unchanged).
 * Role-lensed: accepts a role prop for role-appropriate labeling/visibility.
 * Deep-linkable: /case/:id/:stage pattern.
 */

import React, { useEffect, useState } from 'react';
import { Outlet, useParams, useLocation, Navigate } from 'react-router-dom';
import { isFeatureEnabled } from '@/config/featureFlags';
import { CaseHeader, WorkspaceRole } from './CaseHeader';
import { WorkspaceTabBar, WorkspaceTab } from './WorkspaceTabBar';
import { getPanelForStage } from './WorkspaceStagePanel';
import { normalizeStageForRole } from './stage-strip';
import { AccountantStageNav } from './AccountantStageNav';
import { AttorneyStageNav } from './AttorneyStageNav';
import { AdjusterStageNav } from './AdjusterStageNav';
import { DataSpecialistStageNav } from './DataSpecialistStageNav';
import { api } from '../../utils/api';
import { getCaseProfile } from '../../services/caseProfileService';
import { getCasePipelineStatus } from '../../services/dashboardApi';
import { useCanonicalNet } from '../../hooks/useCanonicalNet';
import { useAccountantAuth } from '../../contexts/AccountantAuthContext';
import { useAppliedThemePreference } from '../../hooks/useAppliedThemePreference';
import type { CaseProfilePayload } from '@/components/shared/caseProfile';
import WorkDeskUtilityBar from '../workdesk/WorkDeskUtilityBar';
import { UnconfirmedPostureBanner } from '@/components/shared/UnconfirmedPostureBanner';

export interface CaseWorkspaceShellProps {
  /** Role lens for labeling/visibility */
  role?: WorkspaceRole;
  /** Optional className */
  className?: string;
}

// Stage validity + the default fallback are role-aware (see ./stage-strip/stageRegistry):
// accountant → the 8-stage spine; attorney/default → the existing 5 stages (unchanged).

/**
 * Extract the base path for the current case workspace route.
 * Handles all three silo patterns:
 * - /case/:claimId/*
 * - /attorney/case/:caseId/*
 * - /accountant/reports/:reportId/*
 */
function getBasePath(location: string, params: Record<string, string | undefined>): string {
  // Accountant silo: /accountant/reports/:reportId
  if (location.includes('/accountant/reports/')) {
    const reportId = params.reportId;
    if (reportId) return `/accountant/reports/${reportId}`;
  }

  // Attorney silo: /attorney/case/:caseId
  if (location.includes('/attorney/case/')) {
    const caseId = params.caseId;
    if (caseId) return `/attorney/case/${caseId}`;
  }

  // Adjuster silo: /adjuster/case/:caseId
  if (location.includes('/adjuster/case/')) {
    const caseId = params.caseId;
    if (caseId) return `/adjuster/case/${caseId}`;
  }

  // Data-Specialist silo: /data-specialist/case/:caseId
  if (location.includes('/data-specialist/case/')) {
    const caseId = params.caseId;
    if (caseId) return `/data-specialist/case/${caseId}`;
  }

  // Default silo: /case/:claimId
  const claimId = params.claimId || params.caseId || params.reportId;
  if (claimId) return `/case/${claimId}`;

  return '/case';
}

/**
 * Get the normalized case ID from params (handles claimId/caseId/reportId)
 */
function getCaseId(params: Record<string, string | undefined>): string {
  return params.claimId || params.caseId || params.reportId || 'unknown';
}

export function CaseWorkspaceShell({ role = 'default', className = '' }: CaseWorkspaceShellProps) {
  // Feature flag gate: if disabled, render nothing (parent route will show existing content)
  if (!isFeatureEnabled('CASE_WORKSPACE')) {
    return null;
  }

  const params = useParams<{
    claimId?: string;
    caseId?: string;
    reportId?: string;
    stage?: string;
    panel?: string;
  }>();
  const location = useLocation();

  // The case shells mount OUTSIDE AppLayout/WorkspaceShell, so a direct load / refresh of a
  // case URL never runs WorkspaceShell's theme effect — light ("Daylight") would fall back to
  // the dark-first default. Honor the saved cg-theme here so the user's choice sticks on case
  // pages too. Read-only w.r.t. the preference (no toggle on this shell → never writes cg-theme).
  useAppliedThemePreference();

  const caseId = getCaseId(params);
  const basePath = getBasePath(location.pathname, params);
  const activeStage = normalizeStageForRole(role, params.stage);
  // Canonical sworn net (policy_deductions.net_claim) via the role-agnostic /case-figures
  // endpoint. Fills the header figure for NON-attorney roles (e.g. the adjuster lens), whose
  // role-specific branch below does not supply a dollar value — so every seat's case header
  // shows the stamped net, not a blank. Additive: attorney keeps its own demand value.
  const canonicalNet = useCanonicalNet(caseId && caseId !== 'unknown' ? caseId : undefined);
  // The external-CPA (accountant JWT) silo. Its token is rejected by the Clerk-only header
  // endpoints (accountant-payload, pipeline-status), so it reads the dual-auth, invite-scoped
  // collaboration /view instead.
  const isExternalSilo = location.pathname.startsWith('/accountant/reports/');

  // Resolve the case display name for the header (fixes the "Unnamed Claim"
  // fallback). Non-blocking: the header shows immediately and populates when the
  // fetch arrives; on any error it keeps the existing fallback.
  const { getAuthHeader } = useAccountantAuth();
  const [insuredName, setInsuredName] = useState<string | undefined>(undefined);
  // Attorney DRAFT-lifecycle header meta (status/method/demand/event). Populated
  // ONLY for role="attorney" from the attorney case-detail endpoint; the accountant
  // path leaves it undefined (header chip renders only when status is present, so the
  // accountant header is byte-identical). status === "final" => externally attested
  // (Signal C) => the chip flips DRAFT → ATTESTED.
  const [attorneyMeta, setAttorneyMeta] = useState<{
    status?: string;
    demand?: number;
    eventType?: string;
    eventDate?: string;
  }>({});
  const [caseProfile, setCaseProfile] = useState<CaseProfilePayload | null>(null);
  useEffect(() => {
    if (!caseId || caseId === 'unknown') return;
    let cancelled = false;

    // CT-0 0.7 — the posture chip reads GET /case-profile ONLY (the resolve() SSoT),
    // for BOTH roles: attorney via the auto-injected Clerk token, accountant via its
    // own auth header. This replaces the previously attorney-only, embedded
    // `res.case_profile` feed and gives the accountant header its (previously
    // missing) posture chip from the authoritative endpoint.
    getCaseProfile(
      caseId,
      role === 'attorney' ? undefined : { headers: getAuthHeader() },
    ).then((cp) => {
      if (!cancelled) setCaseProfile(cp);
    });

    if (isExternalSilo) {
      api
        .get(`/api/v1/collaboration/reports/${caseId}/view`, {
          headers: getAuthHeader(),
          showErrorToast: false,
        })
        .then((res: any) => {
          if (cancelled || !res) return;
          const name = res.client_name || res.claimant_name || res.data?.client_name;
          if (name) setInsuredName(name);
        })
        .catch(() => {
          /* header keeps its existing fallback */
        });
      return () => {
        cancelled = true;
      };
    }

    if (role === 'attorney') {
      // Clerk-authed via the api util (auto-injects the token); gives name + all
      // header fields in one call.
      api
        .get(`/api/v1/attorney/case/${caseId}`)
        .then((res: any) => {
          if (cancelled || !res) return;
          const name = res.business_name || res.client_name;
          if (name) setInsuredName(name);
          setAttorneyMeta({
            status: res.status,
            demand: typeof res.total_demand === 'number' ? res.total_demand : undefined,
            eventType: res.event_type,
            eventDate: res.event_date,
          });
          // Posture chip is sourced from GET /case-profile above (CT-0 0.7), not
          // from this endpoint's embedded case_profile.
        })
        .catch(() => {
          /* header keeps its existing fallback */
        });
    } else {
      // accountant-payload is case-keyed, so claimant_name is correct for the CPA path.
      api
        .get(`/api/v1/dashboard/accountant-payload/${caseId}`, { headers: getAuthHeader() })
        .then((res: any) => {
          if (cancelled) return;
          const cs = res?.case_summary ?? {};
          const name = cs.claimant_name || cs.business_name;
          if (name) setInsuredName(name);
        })
        .catch(() => {
          /* header keeps its existing fallback */
        });
    }
    // Fallback name source — pipeline status is available to both roles and
    // populates the header when the role-specific endpoint returns no name.
    getCasePipelineStatus(caseId)
      .then((ps) => {
        if (!cancelled && ps?.client_name) {
          setInsuredName((prev) => prev || ps.client_name);
        }
      })
      .catch(() => {
        /* header keeps its existing fallback */
      });

    // Universal name fallback (fill-only) — the single-report endpoint returns
    // client_name and is Clerk-authed, so it resolves the header name for roles
    // (e.g. the adjuster/viewer lens) whose role-specific branch above has no
    // access to accountant-payload. Only fills when the name is still empty, so
    // it never overrides the attorney/accountant/pipeline name. NOT for the attorney:
    // the report-content endpoint is permission-gated (`claims:view`) and report content
    // stays locked until attestation; the attorney's name comes from the firm-gated
    // /attorney/case above (+ pipeline-status).
    if (role !== 'attorney') {
      api
        .get(`/api/v1/reports/${caseId}`, { showErrorToast: false })
        .then((res: any) => {
          if (cancelled || !res) return;
          const name = res.client_name || res.business_name || res.claimant_name;
          if (name) setInsuredName((prev) => prev || name);
        })
        .catch(() => {
          /* header keeps its existing fallback */
        });
    }

    return () => {
      cancelled = true;
    };
  }, [caseId, role, isExternalSilo]);

  // Get the panel component for the current stage (role-aware: accountant = 8 stages)
  const StagePanel = getPanelForStage(activeStage, role);

  return (
    <div
      className={`flex flex-col h-full min-h-screen bg-[var(--color-bg-base)] ${className}`}
      data-testid="case-workspace-shell"
    >
      {role === 'data_specialist' && (
        <WorkDeskUtilityBar
          roleLabel="Data Specialist Case"
          onRefresh={() => window.location.reload()}
          isRefreshing={false}
          roleTag="DATA SPECIALIST"
        />
      )}
      <CaseHeader
        caseId={caseId}
        role={role}
        currentStage={activeStage}
        insuredName={insuredName}
        status={attorneyMeta.status}
        method={canonicalNet.electedMethod ?? undefined}
        methodPending={canonicalNet.loading || !!canonicalNet.error}
        claimValue={attorneyMeta.demand ?? canonicalNet.net ?? undefined}
        eventType={attorneyMeta.eventType}
        lossDate={attorneyMeta.eventDate}
        caseProfile={caseProfile}
      />

      <UnconfirmedPostureBanner profile={caseProfile} className="mx-6 mt-4" />

      {/* Stage navigation is role-shaped: the accountant gets the Case Profile Bar +
          8-stage Stage Strip; the attorney gets the 7-stage Stage Strip (K3 taxonomy,
          no profile bar); default keeps the existing 5-tab WorkspaceTabBar BYTE-IDENTICAL. */}
      {role === 'accountant' ? (
        <AccountantStageNav basePath={basePath} activeStageId={activeStage} caseProfile={caseProfile} />
      ) : role === 'attorney' ? (
        <AttorneyStageNav basePath={basePath} activeStageId={activeStage} />
      ) : role === 'adjuster' ? (
        <AdjusterStageNav basePath={basePath} activeStageId={activeStage} />
      ) : role === 'data_specialist' ? (
        <DataSpecialistStageNav basePath={basePath} activeStageId={activeStage} />
      ) : (
        <WorkspaceTabBar
          activeTab={activeStage as WorkspaceTab}
          basePath={basePath}
        />
      )}

      {/* Main content area. (The empty "sub-rail placeholder" that used to sit
          here rendered three unpopulated tool-slot boxes on every stage — they
          read as render artifacts against the exhibit-clean surface, K3 review
          2026-08-23. Removed until there is real per-stage rail content to show;
          an honest empty > three meaningless squares.) */}
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-auto bg-[var(--color-bg-base)]">
          <React.Suspense fallback={
            <div className="p-8 text-center text-[var(--color-text-muted)]">
              Loading...
            </div>
          }>
            <Outlet />
            <StagePanel caseId={caseId} />
          </React.Suspense>
        </main>
      </div>
    </div>
  );
}

/**
 * Wrapper component that conditionally renders the workspace shell based on feature flag.
 * This allows routes to use it as a layout component without affecting existing behavior
 * when the flag is disabled.
 */
export function CaseWorkspaceLayout({ children, role = 'default' }: { children?: React.ReactNode; role?: WorkspaceRole }) {
  if (!isFeatureEnabled('CASE_WORKSPACE')) {
    // Return children unchanged when flag is disabled
    return <>{children}</>;
  }

  return <CaseWorkspaceShell role={role} />;
}

export default CaseWorkspaceShell;
